import type { Being, BeingState } from '../../../models/Being';
import type { Surroundings } from '../../../models/Surroundings';
import type { IBeingState } from './IBeingState';
import { AI, DirectionsVectors } from '../../../models/constants';
import { directionToward } from '../scoring';
import { EatingState } from './EatingState';
import { RestingState } from './RestingState';
import { FleeingState } from './FleeingState';
import { ExploringState } from './ExploringState';
import type { PubSub } from '../../../PubSub';
import type { GameEvents } from '../../../events';

export class SocializingState implements IBeingState {
    static readonly stateName = 'Socializing';

    /**
     * Probability per tick that a Being enters this state when conditions are met.
     * Acts as the species' sociability drive — how often individuals choose to
     * linger near group-mates rather than continuing to explore.
     */
    static readonly SOCIALIZE_PROBABILITY = 0.25;

    /**
     * Probability per tick that a socialising Being reproduces. Rolled once per
     * socialising Being per tick, so the population's growth rate scales with how
     * much of the herd the player keeps clustered and fed.
     */
    static readonly REPRODUCTION_PROBABILITY = 0.003;

    private readonly pubsub: PubSub<GameEvents>;

    /**
     * @param pubsub - Event bus used to publish `world:being-born` when a socialising
     *   being reproduces. The WorldController owns the actual spawn.
     */
    constructor({ pubsub }: { pubsub: PubSub<GameEvents> }) {
        this.pubsub = pubsub;
    }

    get allowInterrupt(): boolean {
        return true;
    }

    canEnter(being: Being, surroundings: Surroundings): boolean {
        if (being.state === EatingState.stateName) return false;
        if (being.state === RestingState.stateName) return false;
        if (being.state === FleeingState.stateName) return false;
        if (surroundings.nearbyBeings.length === 0) return false;
        if (being.health < AI.HUNGER_THRESHOLD) return false;
        if (surroundings.isInDanger) return false;
        // Nobody stops for a chat with a beast in sight — without this, a being that
        // breaks off a hunt immediately socialises while the quarry stands feet away.
        if (surroundings.nearbyBeasts.length > 0) return false;
        return Math.random() < SocializingState.SOCIALIZE_PROBABILITY;
    }

    /**
     * Drifts the Being toward its group-mates and rolls for reproduction. Births are
     * published rather than applied directly: this runs inside the WorldController's
     * being loop, so the newborn is buffered into `pendingBirths` and only added to
     * the model after the loop, preventing it from being ticked in the same step.
     */
    tick(being: Being, surroundings: Surroundings): BeingState {
        if (surroundings.isInDanger) return FleeingState.stateName;
        if (being.health < AI.HUNGER_THRESHOLD) return ExploringState.stateName;
        if (surroundings.nearbyBeings.length === 0) return ExploringState.stateName;

        // Drift toward the centroid of nearby group-mates
        const centroidX = surroundings.nearbyBeings.reduce((s, b) => s + b.x, 0) / surroundings.nearbyBeings.length;
        const centroidZ = surroundings.nearbyBeings.reduce((s, b) => s + b.z, 0) / surroundings.nearbyBeings.length;
        const dir = directionToward(being, { x: centroidX, z: centroidZ });
        being.moveDirection = dir;
        const vec = DirectionsVectors.get(dir)!;
        being.translate(vec.dx * 0.5, vec.dz * 0.5);

        if (Math.random() < SocializingState.REPRODUCTION_PROBABILITY) {
            this.pubsub.publish('world:being-born', { x: being.x, z: being.z });
        }

        return SocializingState.stateName;
    }
}
